import React, { useState, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Form } from 'antd'
import LinkPurple from 'components/design/Form/LinkPurple'
import { encryptEmail } from 'helpers'
import ButtonBlack from '../../design/Form/ButtonBlack'
import constants from '../../../constants'
import style from './style.module.scss'

// Constants for repeated values
const OTP_LENGTH = 6
const OTP_INPUT_WIDTH = 54
const OTP_INPUT_HEIGHT = 54

const LoginOTPForm = ({ headerTitle, footer }) => {
  const [form] = Form.useForm()
  const user = useSelector(state => state.user)
  const [otp, setOTP] = useState(Array(OTP_LENGTH).fill(''))
  const inputsRef = useRef([])
  const dispatch = useDispatch()
  const [timer, setTimer] = useState(0)
  const [isDisabled, setIsDisabled] = useState(false)
  const [isLoginOTPVerificationLoading, setIsLoginOTPVerificationLoading] = useState(false)

  useEffect(() => {
    let timerId
    if (timer > 0) {
      timerId = setInterval(() => {
        setTimer(prevTimer => prevTimer - 1)
      }, 1000)
    } else if (timer === 0) {
      setIsDisabled(false)
    }
    return () => clearInterval(timerId)
  }, [timer])

  // Auto-focus the first OTP input on mount or OTP reset
  useEffect(() => {
    if (inputsRef.current[0]) {
      inputsRef.current[0].focus()
    }
  }, [])

  const onFinish = () => {
    const otpValue = getOtpValue()
    if (otpValue.length === OTP_LENGTH) {
      setIsLoginOTPVerificationLoading(true)
      dispatch({
        type: 'user/LOGIN_OTP',
        payload: { otp: otpValue },
        onComplete: afterLoginEvents,
      })
    } else {
      dispatch({
        type: 'apps/INITIATE_NOTIFICATION',
        payload: {
          type: 'error',
          message: 'Invalid verification code!',
        },
      })
    }
  }

  const afterLoginEvents = ({ error, cancelled, data }) => {
    setIsLoginOTPVerificationLoading(false)
    if (!error && !cancelled && data.data) {
      if (constants.captureGTMEvents && window.CometChatAnalytics) {
        window.CometChatAnalytics.event(constants.GAEvents.user_login)
      }
    }
  }

  // Handle input for each OTP box
  const handleOTPInput = (e, idx) => {
    const { value } = e.target
    // Only allow numeric and single character
    if (!/^[0-9]?$/.test(value)) return
    const newOtp = [...otp]
    newOtp[idx] = value
    setOTP(newOtp)
    // If a digit is entered, move to next input (if not last)
    if (value.length === 1 && idx < OTP_LENGTH - 1) {
      inputsRef.current[idx + 1]?.focus()
    }
    // Auto-submit when last digit is entered
    if (idx === OTP_LENGTH - 1 && value) {
      const otpValue = newOtp.join('')
      if (otpValue.length === OTP_LENGTH) {
        form.submit() // <-- This will call onFinish
      }
    }
  }

  const handleOTPPaste = (e) => {
    const paste = e.clipboardData.getData('text').replace(/\D/g, '')
    if (!paste) return
    const pasteArr = paste.split('').slice(0, OTP_LENGTH)
    const newOtp = [...otp]
    pasteArr.forEach((digit, idx) => {
      newOtp[idx] = digit
      if (inputsRef.current[idx]) {
        inputsRef.current[idx].value = digit
      }
    })
    setOTP(newOtp)
    if (inputsRef.current[pasteArr.length - 1]) {
      inputsRef.current[pasteArr.length - 1].focus()
    }
    if (pasteArr.length === OTP_LENGTH) {
      setTimeout(() => form.submit(), 0)
    }
    e.preventDefault()
  }

  const handleOTPKeyDown = (e, idx) => {
    if (e.key === 'Backspace') {
      if (otp[idx]) {
        const newOtp = [...otp]
        newOtp[idx] = ''
        setOTP(newOtp)
      } else if (idx > 0) {
        inputsRef.current[idx - 1]?.focus()
        const newOtp = [...otp]
        newOtp[idx - 1] = ''
        setOTP(newOtp)
      }
    } else if (e.key === 'ArrowLeft' && idx > 0) {
      inputsRef.current[idx - 1]?.focus()
    } else if (e.key === 'ArrowRight' && idx < OTP_LENGTH - 1) {
      inputsRef.current[idx + 1]?.focus()
    }
  }

  // Combine OTP for submit
  const getOtpValue = () => otp.join('')

  const handleOnClickResend = () => {
    setOTP(Array(OTP_LENGTH).fill(''))
    setTimeout(() => {
      if (inputsRef.current[0]) {
        inputsRef.current[0].focus()
      }
    }, 0)
    dispatch({
      type: 'user/LOGIN',
      payload: { email: user.loginEmail, password: user.loginPassword },
      onComplete: afterResendOTP,
    })
  }

  const afterResendOTP = ({ error, cancelled, data }) => {
    if (!error && !cancelled && data.data) {
      setTimer(30) // Set the timer to 30 seconds
      setIsDisabled(true) // Disable the button
      dispatch({
        type: 'apps/INITIATE_NOTIFICATION',
        payload: {
          type: 'success',
          message:
            'We’ve sent a new verification code to your email. Please check your inbox to continue.',
        },
      })
    }
  }

  return (
    <div className={style.cometchatLogin}>
      <div className={style.cometchatLoginContent} style={{ width: 400, height: 112, gap: 0 }}>
        <div className={style.loginFormTitle}>{headerTitle || 'Verify your email'}</div>
        <div className={style.loginInfoText} style={{ marginBottom: '16px' }}>
          We sent a 6-digit code to <br />{' '}
          <span
            className={style.loginInfoText}
            dangerouslySetInnerHTML={{ __html: encryptEmail(user.loginEmail) }}
          />
          . Please enter the <br />
          code below to continue
        </div>
        <div className={style.cometchatLoginForm}>
          <Form form={form} hideRequiredMark onFinish={onFinish} style={{ width: '100%' }}>
            <div
              className={style.verificationCodeLabel}
              style={{ width: '100%', maxWidth: 400, marginBottom: 4 }}
            >
              <div
                style={{
                  fontWeight: 400,
                  fontSize: 14,
                  color: '#414651',
                  textAlign: 'left',
                  fontFamily: 'Satoshi',
                }}
              >
                Verification code
              </div>
              <div
                style={{
                  display: 'flex',
                  gap: 8,
                  justifyContent: 'center',
                  marginBottom: 16,
                  width: '100%',
                  maxWidth: 400,
                }}
              >
                {otp.map((digit, idx) => (
                  <input
                    key={`otp-input-${user.loginEmail || 'user'}-digit${String(idx + 1)}`}
                    ref={el => {
                      inputsRef.current[idx] = el
                    }}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={e => handleOTPInput(e, idx)}
                    onKeyDown={e => handleOTPKeyDown(e, idx)}
                    onPaste={handleOTPPaste}
                    placeholder="-"
                    style={{
                      flex: 1,
                      minWidth: 0,
                      width: OTP_INPUT_WIDTH,
                      height: OTP_INPUT_HEIGHT,
                      textAlign: 'center',
                      fontSize: 24,
                      border: '1px solid #d5d7da',
                      borderRadius: 8,
                      outline: 'none',
                    }}
                    // autoFocus={idx === 0}
                    disabled={isLoginOTPVerificationLoading}
                    aria-label={`OTP digit ${idx + 1}`}
                  />
                ))}
              </div>
            </div>
            <div
              className={style.resendInfoText}
              style={{
                fontFamily: 'Satoshi',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                marginBottom: 8,
                height: '20px',
                width: '100%',
              }}
            >
              Didn&apos;t receive the email?&nbsp;
              {!isDisabled && (
                <LinkPurple
                  linktext="Click to resend"
                  onClick={handleOnClickResend}
                  to="/verify-account"
                />
              )}
              {isDisabled && (
                <div style={{ color: 'grey', cursor: 'not-allowed' }}>
                  Resend in {timer} seconds
                </div>
              )}
            </div>
            <ButtonBlack
              buttonStyles={{
                height: '44px',
                fontSize: '16px',
                fontWeight: '600',
                marginTop: '24px',
                width: '100%',
                borderRadius: '12px',
              }}
              buttonText="Continue"
              buttonType="submit"
              loading={isLoginOTPVerificationLoading}
              holderStyles={{ margin: '0px' }}
            />
            <span
              style={{
                display: 'block',
                marginTop: 16,
                textAlign: 'center',
                fontWeight: 400,
                fontSize: '16px',
                color: '#535862',
                lineHeight: '24px',
                fontStyle: 'normal',
                fontFamily: 'Satoshi',
              }}
            >
              If you’ve lost access to this email,{' '}
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://help.cometchat.com/hc/en-us/requests/new"
                style={{ color: '#6952d6' }}
              >
                Contact support
              </a>
            </span>
          </Form>
        </div>
      </div>
      {footer}
    </div>
  )
}

export default LoginOTPForm
